
import React, { Component, ErrorInfo, ReactNode } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    console.error('ErrorBoundary caught an error:', error);
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error);
    console.error("Error info:", errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  public render() {
    if (this.state.hasError) {
      // Fallback UI when an error occurs
      return this.props.fallback || (
        <div className="min-h-screen flex items-center justify-center p-4 bg-white">
          <Alert className="max-w-md border-red-500">
            <AlertTitle className="text-xl text-red-600">Something went wrong</AlertTitle>
            <AlertDescription className="mt-2 mb-4">
              <div className="text-sm text-red-600 font-bold">
                {this.state.error?.message || "An unexpected error occurred."}
              </div>
              {this.state.errorInfo && (
                <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-[200px]">
                  {this.state.errorInfo.componentStack}
                </pre>
              )}
            </AlertDescription>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  console.log('Refreshing page after error');
                  window.location.reload();
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Refresh Page
              </Button>
              <Button
                onClick={() => {
                  console.log('Navigating to homepage after error');
                  window.location.href = '/';
                }}
                variant="outline"
              >
                Go to Homepage
              </Button>
            </div>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}
